import { Badge, type BadgeVariant } from '@heyroot/ui/primitives/Badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@heyroot/backoffice-ui';
import type { IssuanceStop } from '../../lib/gift-card-issuance-stop';

export const ACCESS_MODES = ['off', 'view', 'full', 'locked'] as const;

function accessModeVariant(mode: string): BadgeVariant {
  switch (mode) {
    case 'off':
      return 'neutral';
    case 'locked':
      return 'warning';
    default:
      return 'success';
  }
}

/**
 * The access-mode cell of one entitlement row: a select for an operator who can write, a badge
 * otherwise. A row with a stop state (gift-card issuance off) shows that in place of its mode.
 */
export function EntitlementAccessCell({
  row,
  stop,
  canWrite,
  pending,
  onChange,
}: {
  row: { moduleKey: string; accessMode: string };
  stop: IssuanceStop | null;
  canWrite: boolean;
  pending: boolean;
  onChange: (accessMode: string) => void;
}) {
  if (!canWrite) {
    return (
      <Badge variant={stop !== null ? 'warning' : accessModeVariant(row.accessMode)}>
        {stop?.label ?? row.accessMode}
      </Badge>
    );
  }
  return (
    <Select value={stop?.value ?? row.accessMode} onValueChange={onChange}>
      <SelectTrigger
        disabled={pending}
        className="min-w-48"
        aria-label={`Access mode for ${row.moduleKey}`}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {stop !== null && (
          <SelectItem value={stop.value} disabled>
            {stop.label}
          </SelectItem>
        )}
        {ACCESS_MODES.map((m) => (
          <SelectItem key={m} value={m} disabled={stop?.blocksFull === true && m === 'full'}>
            {m}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
